import classNames from "classnames";
import * as React from "react";
import { Menu } from "react-aria-menubutton";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";
import { Row } from "react-table";

import EditTenantView from "./edit-tenant/EditTenantView";
import EditMcConfigurationLoader from "./EditMcConfigurationLoader";
import MoveTenantDialog from "./MoveTenantDialog";
import style from "./tenants.scss";
import DeleteConfirmationDialog from "components/confirmation/DeleteConfirmationDialog";
import Warning from "components/icons/Warning";
import { subpageContext } from "components/layout/ApplicationLayout";
import { CorporateBundle, ProcessorBundle } from "components/licenses/bundles";
import { FEATURE_TENANT_MANAGEMENT, fetchDynamicTierLicenses, getFeatureLicense } from "components/licenses/common";
import AddLicenseDeliveryView, {
    showTooltipForDeliveryButton,
} from "components/licenses/delivery-history/AddLicenseDeliveryView";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import MenuItemButton from "components/menu-item-button/MenuItemButton";
import Modal from "components/modal/Modal";
import { DELIVERYHISTORY_LICENSES_ROUTE } from "components/router/Routes";
import AddTenantView from "components/tenants/add-tenant/AddTenantView";
import ViewTenantHierarchy from "components/tenants/tenant-hierarchy/ViewTenantHierarchy";
import Heading from "components/typography/heading/Heading";
import {
    AUTH_LICENSE_ALERT,
    AUTH_LICENSE_ASSIGN,
    AUTH_TENANT_ACCESS,
    AUTH_TENANT_CREATE,
    AUTH_TENANT_DELETE,
    AUTH_TENANT_EDIT,
    AUTH_TENANT_VIEW,
} from "domain/authority";
import { SUPPORT_EMAIL } from "domain/globalConstants";
import {
    CombinedTier,
    ContractType,
    LicensingModel,
    Tenant,
    TenantContractStatus,
    TenantListItem,
    TenantType,
} from "domain/tenants";
import { licenseService, ProductToRateList } from "services/licenses/LicenseService";
import { setTenantAccessUrls } from "services/login/endpointRepository";
import { Action, Category, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import { hasSubTenantCookie, hasTenantCookie, isCurrentUserInternal } from "services/tenants/tenantCookieService";
import { tenantService } from "services/tenants/TenantService";
import { userSessionService } from "services/user/UserSessionService";
import { StoreState } from "store";
import { pushTenantDetails, updateTenantDetails } from "store/tenantDetails";
import { switchTheme } from "store/theme";
import buttons from "styles/buttons.scss";
import form from "styles/form.scss";
import { formatDateWithoutTime, toUtcEndOfDay } from "utils/format";

import testIds from "testIds.json";

interface Result {
    title: string;
    message: string;
    resultVisible: boolean;
}

const mapState = (state: StoreState) => ({
    themeName: state.themeReducer.themeName,
    theme: state.themeReducer.theme,
    user: state.userReducer.user,
});

const connector = connect(mapState, { switchTheme, pushTenantDetails, updateTenantDetails });

const TenantMenuItems = (
    props: ConnectedProps<typeof connector> & {
        uuid: string;
        customerName: string;
        status: boolean;
        onTenantEdit: () => void;
        onTenantDelete: () => void;
        tenantRegion: string;
        expirationDate: string;
        countryCode: string;
        contactName: string;
        contactEmail: string;
        creationDate: string;
        salesforceLeadId: string;
        notes: string;
        tier: CombinedTier;
        salesforceAccountId: string;
        parentExpirationDate: string;
        licenseCount: number;
        userCount: number;
        workflowCount: number;
        reportCount: number;
        parentLevel: number;
        licenses: string[];
        showMenuAbove?: boolean;
        tenantType: TenantType;
        rowsUntilSubTenant: Row<Tenant>[];
        setNewComponent: (value: boolean) => void;
        setTenantUuid: (value: string) => void;
        setTenantName: (value: string) => void;
        licensingModel: LicensingModel;
        rateUuid?: string;
        deliveryAllowed: boolean;
        parentLicensingModel: LicensingModel;
        rateVersions?: ProductToRateList[];
        parentType: TenantType;
        parentTier: CombinedTier;
        salesforceContractId?: string;
        salesforceContractState?: TenantContractStatus | string;
        salesforceContactEmail?: string;
        salesforceContractEndDate?: string;
        parentDrawBackPrevention: boolean;
        drawbackPrevention?: boolean;
        contractType: ContractType;
        parentContractType: ContractType;
        automaticDeletion: boolean;
        diskQuotaMib?: number;
        uploadLimitMib?: number;
        downloadLimitMib?: number;
        depth: number;
        rowTenantLevel: number;
        trialDaysLeft?: null | number;
        tenants: TenantListItem[];
    }
): JSX.Element => {
    const { t } = useTranslation();
    const [deleteTenantModalVisible, setDeleteTenantModalVisible] = React.useState(false);
    const [subTenantModalVisible, setSubTenantModalVisible] = React.useState(false);
    const [addTenantModalVisible, setAddTenantModalVisible] = React.useState(false);
    const [retryModalVisible, setRetryModalVisible] = React.useState(false);
    const [licenseNotAvailable, setLicenseNotAvailable] = React.useState(false);
    const [result, setResult] = React.useState<Result>({
        title: "",
        message: "",
        resultVisible: false,
    });
    const TENANT_MAX_LEVEL = 5;
    const [accessInProgress, setAccessInProgress] = React.useState(false);
    const { current: abortControllers } = React.useRef<AbortController[]>([]);
    const [accessTenantModalVisible, setAccessTenantModalVisible] = React.useState(false);
    const context = React.useContext(subpageContext);
    const hideResultAndRedirectToRoot = () => {
        setResult({ title: result.title, message: result.message, resultVisible: false });

        if (hasTenantCookie()) {
            window.location.replace(location.pathname);
        }

        props.onTenantEdit();
        props.onTenantDelete();
    };
    const [isMcConfigurationVisible, setIsMcConfigurationVisible] = React.useState(false);
    const [moveTenantModalVisible, setMoveTenantModalVisible] = React.useState(false);
    const subtenantCreationAllowed: boolean =
        props.contractType === "TRIAL" ? props.trialDaysLeft != null && props.trialDaysLeft > 0 : true;

    const fetchData = () => {
        setAccessInProgress(true);
        const landingPage = props.user?.default_settings.tenant_access_default_view;
        const abortController = new AbortController();
        abortControllers.push(abortController);
        if (props.rowsUntilSubTenant.length > 0) {
            props.rowsUntilSubTenant.forEach((eachRow) => {
                const tenant = eachRow.original;
                props.pushTenantDetails({
                    uuid: tenant.uuid,
                    featureLicenses: [],
                    region: tenant.region,
                    type: tenant.type,
                    tenantName: tenant.name,
                    loginMethod: tenant.loginMethod,
                    tenantTier: tenant.tier,
                    licensingModel: tenant.licensingModel,
                    parentDrawBackPrevention: tenant.parentDrawbackPrevention,
                    contractType: tenant.contractType,
                    expirationDate: tenant.expirationDate,
                });
            });
        }
        tenantService
            .accessTenant(props.uuid, abortController)
            .then((data) => {
                setTenantAccessUrls(
                    data.stan,
                    data.laurel,
                    data.arthur,
                    data.jefferson,
                    data.oliver,
                    data.publicApi,
                    data.publicApiDocumentation
                );
                props.pushTenantDetails({
                    uuid: props.uuid,
                    featureLicenses: data.featureLicenses,
                    region: data.region,
                    type: data.type,
                    tenantName: data.tenantName,
                    loginMethod: data.loginMethod,
                    tenantTier: data.tenantTier,
                    licensingModel: data.licensingModel,
                    parentDrawBackPrevention: data.parentDrawbackPrevention,
                    contractType: data.contractType,
                    expirationDate: data.expirationDate,
                });
                props.switchTheme();
                const checkFeatureLicense = getFeatureLicense();
                const checkIfFeatureLicensePresent: boolean = checkFeatureLicense.has(landingPage)
                    ? data.featureLicenses.some((item) => checkFeatureLicense.get(landingPage)?.includes(item))
                    : true;
                hasTenantCookie() &&
                    window.location.replace(
                        !checkIfFeatureLicensePresent
                            ? DELIVERYHISTORY_LICENSES_ROUTE.path
                            : props.user?.default_settings.tenant_access_default_view
                              ? props.user.default_settings.tenant_access_default_view
                              : "/"
                    );
                setResult({
                    title: t("AccessCustomerView.accessTitle"),
                    message: t("AccessCustomerView.accessSuccessMessage", { CustomerName: props.customerName }),
                    resultVisible: true,
                });
            })
            .catch(() => {
                if (!abortController.signal.aborted) {
                    setResult({
                        title: t("AccessCustomerView.accessTitle"),
                        message: t("AccessCustomerView.accessFailureMessage", { customerName: props.customerName }),
                        resultVisible: true,
                    });
                }
            })
            .finally(() => {
                setAccessInProgress(false);
                setAccessTenantModalVisible(false);
            });
    };

    const [okClicked, setOkClicked] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const [hierarchyVisibility, setHierarchyVisibility] = React.useState(false);

    const [checkHierarchy, setCheckHierarchy] = React.useState(false);

    const handleTenantDelete = () => {
        setOkClicked(true);
        deleteTenant();
    };

    const deleteTenant = async () => {
        const abortController = new AbortController();
        abortControllers.push(abortController);
        const { signal } = abortController;
        try {
            await tenantService.deleteTenant(props.uuid, props.tenantRegion, abortController);
        } catch {
            if (!signal.aborted) {
                setOkClicked(false);
                setDeleteTenantModalVisible(false);
                setSubTenantModalVisible(false);
                setRetryModalVisible(true);
            }
            return;
        }
        if (signal.aborted) {
            return;
        }
        setDeleteTenantModalVisible(false);
        setSubTenantModalVisible(false);
        setRetryModalVisible(false);
        setOkClicked(false);
        setResult({
            title: t("DeleteTenant.inProgressTitle"),
            message: t("DeleteTenant.inProgressMessage"),
            resultVisible: true,
        });
    };

    const checkTenantHierarchy = async () => {
        setLoading(true);
        const abortController = new AbortController();
        abortControllers.push(abortController);
        await tenantService
            .fetchHierarchy(props.uuid, abortController)
            .then((response) => {
                response?.tenants.length == 0 || undefined
                    ? setDeleteTenantModalVisible(true)
                    : setSubTenantModalVisible(true);
                setLoading(false);
            })
            .catch(() => {
                setCheckHierarchy(true);
                setRetryModalVisible(true);
                setLoading(false);
            });
    };

    function updateState() {
        context?.setSubpage({
            content: undefined,
        });
        setLoading(false);
        setAddTenantModalVisible(false);
    }

    function updateStateWhenLicenseAdded() {
        setLoading(false);
        setDeliveryCreationModalVisible(false);
    }

    const [deliveryCreationModalVisible, setDeliveryCreationModalVisible] = React.useState(false);

    const subject = encodeURIComponent(
        "Blancco Management Portal - Unexpected Error while deleting tenant : " + props.customerName
    );
    const body = encodeURIComponent(
        "Hi there\n\n" +
            "I've experienced a persistent issue with deleting tenant: " +
            props.customerName +
            "\nWould you be able to assist?\n\n"
    );
    const mailToLink = `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`;

    async function showAddTenantFormModal() {
        setLoading(true);
        usageStatisticsService.sendEvent({
            category: Category.TENANT,
            action: Action.ADD_SUB_TENANT,
        });
        let foundTenantManagementLicense = false;
        if (props.tier != undefined) {
            const tierLicenses = fetchDynamicTierLicenses(props.tier);
            if (tierLicenses != undefined && tierLicenses.includes(FEATURE_TENANT_MANAGEMENT)) {
                foundTenantManagementLicense = !(
                    props.tier === CorporateBundle.POWER || props.tier === ProcessorBundle.PRO
                );
            }
        }
        const licenseResponse = await licenseService.fetchLicenses(
            licensesAbortController,
            undefined,
            undefined,
            props.uuid
        );
        if (
            foundTenantManagementLicense ||
            licenseResponse.licenses.some((each) => each.type === FEATURE_TENANT_MANAGEMENT)
        ) {
            setLoading(false);
            setAddTenantModalVisible(true);
        } else {
            setLoading(false);
            setLicenseNotAvailable(true);
        }
    }

    const licensesAbortController = new AbortController();
    abortControllers.push(licensesAbortController);

    const hideErrorModal = () => {
        setLicenseNotAvailable(false);
    };

    const deliveryItemId = "deliveryItemButton";
    function createMenuItemForDelivery() {
        return (
            <li className={style.listItem} id={deliveryItemId}>
                <MenuItemButton
                    onClick={() => {
                        setDeliveryCreationModalVisible(true);
                        usageStatisticsService.sendEvent({
                            category: Category.LICENSE_DELIVERY,
                            action: Action.ADD_LICENSE_DELIVERY,
                        });
                    }}
                    disabled={!props.deliveryAllowed}
                    className={classNames(style.menuItem)}
                    data-testid={testIds.workArea.tenant.table.kebabMenu.createDeliveryButton}
                    data-for={deliveryItemId}
                    data-tip
                >
                    {t("AddLicenseDelivery.title")}
                </MenuItemButton>
            </li>
        );
    }

    const parentExpirationDate = props.expirationDate
        ? new Date(toUtcEndOfDay(new Date(formatDateWithoutTime(props.expirationDate))))
        : undefined;
    {
        addTenantModalVisible &&
            context?.setSubpage({
                content: (
                    <AddTenantView
                        onHideModal={updateState}
                        onTenantAdded={updateState}
                        parentExpirationDate={parentExpirationDate}
                        parentLevel={props.rowTenantLevel}
                        parentLicenses={undefined}
                        parentUuid={props.uuid}
                        tier={props.tier}
                        type={props.tenantType}
                        licensingModel={props.licensingModel}
                        rateVersions={props.rateVersions}
                        parentDrawbackPrevention={props.parentDrawBackPrevention}
                        parentContractType={props.parentContractType}
                        parentRateUuid={props.rateUuid}
                    />
                ),
                isModal: false,
            });
    }

    return (
        <div
            className={classNames(
                style.tenantIcon,
                props.showMenuAbove ? style.kebabMenuAtBottom : style.kebabMenuAtTop
            )}
        >
            {showTooltipForDeliveryButton(props.deliveryAllowed, t, deliveryItemId)}
            <Menu className={style.kebabMenu}>
                <ul>
                    {userSessionService.hasFeatureLicense("FEATURE_TENANT_MANAGEMENT") &&
                        userSessionService.userHasAllAuthorities([AUTH_TENANT_ACCESS]) && (
                            <li className={style.listItem}>
                                <MenuItemButton
                                    onClick={() => {
                                        setAccessTenantModalVisible(true);
                                        usageStatisticsService.sendEvent({
                                            category: Category.TENANT,
                                            action: Action.ACCESS_TENANT,
                                        });
                                    }}
                                    className={style.menuItem}
                                    data-testid={testIds.workArea.tenant.table.kebabMenu.accessButton}
                                >
                                    {t("AccessCustomerView.accessCustomerAccount")}
                                </MenuItemButton>
                            </li>
                        )}
                    {userSessionService.hasFeatureLicense("FEATURE_TENANT_MANAGEMENT") &&
                        userSessionService.userHasAllAuthorities([AUTH_TENANT_EDIT]) && (
                            <li className={style.listItem}>
                                <MenuItemButton
                                    onClick={() => {
                                        context?.setSubpage({
                                            content: (
                                                <EditTenantView
                                                    customerName={props.customerName}
                                                    status={props.status}
                                                    uuid={props.uuid}
                                                    tier={props.tier}
                                                    tenantRegion={props.tenantRegion}
                                                    expirationDate={props.expirationDate}
                                                    countryCode={props.countryCode}
                                                    contactName={props.contactName}
                                                    contactEmail={props.contactEmail}
                                                    creationDate={props.creationDate}
                                                    salesforceLeadId={props.salesforceLeadId}
                                                    notes={props.notes}
                                                    salesforceAccountId={props.salesforceAccountId}
                                                    parentExpirationDate={props.parentExpirationDate}
                                                    tenantType={props.tenantType}
                                                    licensingModel={props.licensingModel}
                                                    parentLicensingModel={props.parentLicensingModel}
                                                    parentType={props.parentType}
                                                    parentTier={props.parentTier}
                                                    salesforceContractState={props.salesforceContractState}
                                                    salesforceContractId={props.salesforceContractId}
                                                    salesforceContactEmail={props.salesforceContactEmail}
                                                    salesforceContractEndDate={props.salesforceContractEndDate}
                                                    parentDrawbackPrevention={props.parentDrawBackPrevention}
                                                    drawbackPrevention={props.drawbackPrevention}
                                                    contractType={props.contractType}
                                                    automaticDeletion={props.automaticDeletion}
                                                    parentContractType={props.parentContractType}
                                                    diskQuotaMib={props.diskQuotaMib}
                                                    uploadLimitMib={props.uploadLimitMib}
                                                    downloadLimitMib={props.downloadLimitMib}
                                                    currentRateUuid={props.rateUuid}
                                                    rateVersions={props.rateVersions}
                                                />
                                            ),
                                            isModal: false,
                                        });
                                    }}
                                    className={style.menuItem}
                                    data-testid={testIds.workArea.tenant.table.kebabMenu.editButton}
                                >
                                    {t("EditCustomerView.edit")}
                                </MenuItemButton>
                            </li>
                        )}
                    {userSessionService.hasFeatureLicense("FEATURE_TENANT_MANAGEMENT") &&
                        userSessionService.userHasAllAuthorities([AUTH_TENANT_EDIT]) &&
                        isCurrentUserInternal() &&
                        props.status && (
                            <li className={style.listItem}>
                                <MenuItemButton
                                    onClick={() => {
                                        usageStatisticsService.sendEvent({
                                            category: Category.TENANT,
                                            action: Action.MOVE_TENANT,
                                        });
                                        setMoveTenantModalVisible(true);
                                    }}
                                    className={style.menuItem}
                                    data-testid={testIds.workArea.tenant.table.kebabMenu.moveButton}
                                >
                                    {t("EditCustomerView.move.move")}
                                </MenuItemButton>
                            </li>
                        )}
                    {isCurrentUserInternal() && userSessionService.userHasAllAuthorities([AUTH_TENANT_EDIT]) && (
                        <li className={style.listItem}>
                            <MenuItemButton
                                onClick={() => {
                                    usageStatisticsService.sendEvent({
                                        category: Category.TENANT,
                                        action: Action.EDIT_MC_CONFIGURATION,
                                    });
                                    setIsMcConfigurationVisible(true);
                                }}
                                className={style.menuItem}
                                data-testid={testIds.workArea.tenant.table.kebabMenu.mcSettingsButton}
                            >
                                {t("EditCustomerView.editMcConfiguration")}
                            </MenuItemButton>
                        </li>
                    )}
                    {userSessionService.hasFeatureLicense("FEATURE_TENANT_MANAGEMENT") &&
                        userSessionService.userHasAllAuthorities([
                            AUTH_TENANT_VIEW,
                            AUTH_TENANT_EDIT,
                            AUTH_TENANT_ACCESS,
                            AUTH_TENANT_CREATE,
                        ]) && (
                            <li className={style.listItem}>
                                <MenuItemButton
                                    onClick={() => {
                                        setHierarchyVisibility(true);
                                        usageStatisticsService.sendEvent({
                                            category: Category.TENANT,
                                            action: Action.VIEW_TENANT_HIERARCHY,
                                        });
                                    }}
                                    className={style.menuItem}
                                    data-testid={testIds.workArea.tenant.table.kebabMenu.hierarchyButton}
                                >
                                    {t("ViewTenantHierarchy.iconTooltip")}
                                </MenuItemButton>
                            </li>
                        )}
                    {userSessionService.hasFeatureLicense("FEATURE_TENANT_MANAGEMENT") &&
                        userSessionService.userHasAllAuthorities([AUTH_LICENSE_ASSIGN, AUTH_TENANT_ACCESS]) &&
                        createMenuItemForDelivery()}
                    {userSessionService.hasFeatureLicense("FEATURE_TENANT_MANAGEMENT") &&
                        userSessionService.userHasAllAuthorities([AUTH_TENANT_CREATE, AUTH_TENANT_ACCESS]) &&
                        (hasTenantCookie() || hasSubTenantCookie()
                            ? props.depth + props.parentLevel < TENANT_MAX_LEVEL
                            : props.depth < TENANT_MAX_LEVEL - 1) &&
                        subtenantCreationAllowed && (
                            <li className={style.listItem}>
                                <MenuItemButton
                                    onClick={showAddTenantFormModal}
                                    className={style.menuItem}
                                    data-testid={testIds.workArea.tenant.table.kebabMenu.createTenantButton}
                                >
                                    {t("Common.addSubTenant")}
                                </MenuItemButton>
                            </li>
                        )}
                    {userSessionService.hasFeatureLicense("FEATURE_LICENSE_ALERTS") &&
                        userSessionService.userHasAllAuthorities([AUTH_LICENSE_ALERT, AUTH_TENANT_ACCESS]) && (
                            <li className={style.listItem}>
                                <MenuItemButton
                                    onClick={() => {
                                        props.setNewComponent(true);
                                        props.setTenantUuid(props.uuid);
                                        props.setTenantName(props.customerName);
                                    }}
                                    className={style.menuItem}
                                    data-testid={testIds.workArea.tenant.table.kebabMenu.createLicenseAlertButton}
                                >
                                    {t("LicenseAlerts.create")}
                                </MenuItemButton>
                            </li>
                        )}
                    {userSessionService.hasFeatureLicense("FEATURE_TENANT_MANAGEMENT") &&
                        userSessionService.userHasAllAuthorities([AUTH_TENANT_DELETE]) && (
                            <li className={style.deleteListItem}>
                                <MenuItemButton
                                    onClick={() => {
                                        checkTenantHierarchy();
                                        usageStatisticsService.sendEvent({
                                            category: Category.TENANT,
                                            action: Action.DELETE_TENANT,
                                        });
                                    }}
                                    className={classNames(style.menuItem, style.deleteListItem)}
                                    data-testid={testIds.workArea.tenant.table.kebabMenu.deleteButton}
                                >
                                    {t("CustomersTable.deleteCustomer", { customerName: props.customerName })}
                                </MenuItemButton>
                            </li>
                        )}
                </ul>
            </Menu>
            <Modal
                isOpen={accessTenantModalVisible}
                hideModal={() => setAccessTenantModalVisible(false)}
                modalTitle={t("AccessCustomerView.title")}
            >
                <div className={style.resultContainer}>
                    {t("AccessCustomerView.message", { customerName: props.customerName })}
                </div>

                {accessInProgress ? (
                    <LoadingIndicator />
                ) : (
                    <div className={buttons.buttonContainer}>
                        <button
                            className={buttons.secondaryButtonWithoutIcon}
                            onClick={() => setAccessTenantModalVisible(false)}
                        >
                            {t("Common.cancel")}
                        </button>

                        <button className={buttons.okOptionalButtonPrimary} onClick={fetchData}>
                            {t("Common.ok")}
                        </button>
                    </div>
                )}
            </Modal>
            <Modal isOpen={result.resultVisible} hideModal={hideResultAndRedirectToRoot} modalTitle={result.title}>
                <div className={style.resultContainer}>{result.message}</div>
                <div className={style.warningContainer}>
                    <button
                        className={buttons.primaryOkButton}
                        onClick={hideResultAndRedirectToRoot}
                        data-testid={testIds.common.confirmationDialog.confirmButton}
                    >
                        {t("Common.ok")}
                    </button>
                </div>
            </Modal>
            <Modal
                isOpen={hierarchyVisibility}
                hideModal={() => setHierarchyVisibility(false)}
                modalTitle={t("ViewTenantHierarchy.title", { tenantName: props.customerName })}
            >
                {<ViewTenantHierarchy uuid={props.uuid} />}
            </Modal>
            <DeleteConfirmationDialog
                modalDisplayed={deleteTenantModalVisible}
                introductionMessage={
                    <div>
                        <p>
                            {t("DeleteTenant.introductionMessagePre")}
                            <b>{props.customerName}</b>
                            {props.expirationDate == undefined || props.expirationDate == ""
                                ? t("DeleteTenant.introductionMessageWithoutDate")
                                : t("DeleteTenant.introductionMessageWithDate", {
                                      expDate: formatDateWithoutTime(props.expirationDate),
                                  })}
                            <ul className={style.listStyleType}>
                                <li>{t("DeleteTenant.licenseCount", { licenseCount: props.licenseCount })}</li>
                                <li>{t("DeleteTenant.userCount", { userCount: props.userCount })}</li>
                                <li>{t("DeleteTenant.reportCount", { reportCount: props.reportCount })}</li>
                                <li>
                                    {t("DeleteTenant.workflowCount", {
                                        workflowCount: props.workflowCount,
                                    })}
                                </li>
                            </ul>
                            {t("DeleteTenant.introductionMessagePost")}
                        </p>
                    </div>
                }
                confirmationMessage={t("DeleteTenant.confirmationMessage")}
                handleCommand={handleTenantDelete}
                setModalDisplayed={() => setDeleteTenantModalVisible(false)}
                title={t("DeleteTenant.title")}
                loading={okClicked || loading}
                drawbackPreventionMessage={
                    props.parentDrawBackPrevention ? (
                        <div className={classNames(form.warningBanner, form.fixedWidthModal)}>
                            <div className={classNames(form.formFields)}>
                                <Warning color={props.theme.secondaryWarningBackgroundColor} width={45} height={20} />
                                {t("DeleteTenant.drawbackPreventionWarningMessage")}
                            </div>
                        </div>
                    ) : undefined
                }
            />
            {deliveryCreationModalVisible && (
                <AddLicenseDeliveryView
                    tenantUuid={props.uuid}
                    tenantName={props.customerName}
                    onLicenseAdded={updateState}
                    onHideModal={updateStateWhenLicenseAdded}
                    tenantTier={props.tier}
                    tenantType={props.tenantType}
                    licensingModel={props.licensingModel}
                    tenantRateUuid={props.rateUuid}
                    allowed={props.deliveryAllowed}
                    parentDrawbackPrevention={props.parentDrawBackPrevention}
                    contractType={props.contractType}
                />
            )}
            <Modal isOpen={subTenantModalVisible} hideModal={() => setSubTenantModalVisible(false)}>
                {okClicked ? (
                    <LoadingIndicator />
                ) : (
                    <>
                        {subTenantModalVisible ? (
                            <>
                                <div className={style.warningContainer}>
                                    <Warning color={props.theme.secondaryWarningBackgroundColor} />
                                </div>
                                <div className={style.subTenant}>
                                    <Heading tag={"h2"}>{t("DeleteTenant.subTenantMessage")}</Heading>
                                </div>
                                <div className={style.warningContainer}>{t("DeleteTenant.subTenantErrorMessage")}</div>
                                <div className={style.warningContainer}>
                                    <button
                                        className={buttons.primaryOkButton}
                                        onClick={() => setSubTenantModalVisible(false)}
                                        data-testid={testIds.common.dialog.closeButton}
                                    >
                                        {t("Common.ok")}
                                    </button>
                                </div>
                            </>
                        ) : (
                            ""
                        )}
                    </>
                )}
            </Modal>
            <Modal isOpen={retryModalVisible} hideModal={() => setRetryModalVisible(false)}>
                {okClicked ? (
                    <LoadingIndicator />
                ) : (
                    <>
                        {retryModalVisible ? (
                            <>
                                <div className={style.warningContainer}>
                                    <Warning color={props.theme.errorBackgroundColor} />
                                </div>
                                <div className={style.subTenant}>{t("DeleteTenant.deletionFailed")}</div>
                                <div className={style.warningContainer}>
                                    {t("DeleteTenant.deletionFailedMessage")}
                                    <span>
                                        <a href={mailToLink}>{t("DeleteTenant.supportLink")}</a>
                                    </span>
                                </div>
                                <div className={style.warningContainer}>
                                    {checkHierarchy ? (
                                        <button
                                            className={buttons.primaryOkButton}
                                            onClick={() => {
                                                setRetryModalVisible(false);
                                                setCheckHierarchy(false);
                                            }}
                                            data-testid={testIds.common.dialog.closeButton}
                                        >
                                            {t("Common.ok")}
                                        </button>
                                    ) : (
                                        <>
                                            <div className={buttons.buttonContainer}>
                                                <button
                                                    className={classNames(
                                                        buttons.primaryButtonWithoutIcon,
                                                        style.deleteButton
                                                    )}
                                                    data-testid={testIds.common.confirmationDialog.confirmButton}
                                                    onClick={handleTenantDelete}
                                                >
                                                    {t("Common.deleteAgain")}
                                                </button>
                                                <button
                                                    className={buttons.secondaryButtonWithoutIcon}
                                                    onClick={() => setRetryModalVisible(false)}
                                                    data-testid={testIds.common.dialog.closeButton}
                                                >
                                                    {t("Common.cancel")}
                                                </button>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </>
                        ) : (
                            ""
                        )}
                    </>
                )}
            </Modal>
            <Modal isOpen={loading} hideModal={() => setLoading(false)}>
                <LoadingIndicator />
            </Modal>
            <Modal
                isOpen={licenseNotAvailable}
                hideModal={hideErrorModal}
                modalTitle={t("AddCustomerView.subTenantCreationFailure")}
            >
                <>
                    <div className={style.errorMessageContainer}>
                        {t("AddCustomerView.licenseMissingError")}
                        <span>
                            <a href={mailToLink}>{t("AddCustomerView.supportText")}</a>
                        </span>
                    </div>
                    <div className={style.errorButtonContainer}>
                        <button
                            className={classNames(
                                buttons.primaryButton,
                                buttons.medium,
                                buttons.okButton,
                                buttons.buttonWithoutIcon
                            )}
                            onClick={hideErrorModal}
                            data-testid={testIds.common.dialog.closeButton}
                        >
                            {t("Common.ok")}
                        </button>
                    </div>
                </>
            </Modal>
            <MoveTenantDialog
                displayMoveTenantModal={moveTenantModalVisible}
                introductionMessage={
                    <div className={style.introductionMessage}>
                        {t("EditCustomerView.move.fromTenant")}
                        <b> &quot;{props.customerName}&quot; </b>
                        {t("EditCustomerView.move.toTenant")}
                    </div>
                }
                setDisplayMoveTenantModal={() => setMoveTenantModalVisible(false)}
                title={t("EditCustomerView.move.moveTenant")}
                loading={okClicked || loading}
                tenants={props.tenants}
                currentTenantId={props.uuid}
                customerName={props.customerName}
                currentTenantTier={props.tier}
                tenantUuid={props.uuid}
                onRefresh={props.onTenantEdit}
                currentTenantLicensingModel={props.licensingModel}
                currentTenantType={props.tenantType}
            />
            {isMcConfigurationVisible && (
                <EditMcConfigurationLoader
                    hide={() => setIsMcConfigurationVisible(false)}
                    tenantUuid={props.uuid}
                    tenantRegion={props.tenantRegion}
                />
            )}
        </div>
    );
};

export default connector(TenantMenuItems);
